// Reads data/tokens.csv into token definitions.
// Columns: id, name, category, image, counter, start, bar, shape, size
//  - category: damage, resource, status, misc, affiliation or dice (sets the spawn-menu section;
//              dice are six-sided dice you click to roll or fling to throw)
//  - counter: yes = shows a number you click up and down; no = a plain token
//  - start: the number a counter starts at
//  - bar: gerzite or dark = new tokens drop into the next free slot of that bar
//  - shape: round = a round chip with the image on top;
//           cutout = the token takes the exact outline of its PNG (transparent parts disappear)
//  - size: width on the table in cm (blank = the default token size in src/config.ts)
//  - effect: which built-in ambient effect and behavior the token has (burn, frozen,
//            divinity…). Blank = the same as its id. See README for the full list.
//  - full_at: for counters whose effect grows with the number: the value at which the
//            effect is at full strength (blank = the effect doesn't change with the number)
import { csvToRecords } from './csv.ts';

export const TOKEN_CATEGORIES = ['damage', 'resource', 'status', 'misc', 'affiliation', 'dice'] as const;

export interface TokenDef {
  id: string;
  name: string;
  category: string;
  image: string;
  counter: boolean;
  start: number;
  bar: '' | 'gerzite' | 'dark';
  shape: 'round' | 'cutout';
  size: number | null;
  effect: string;
  fullAt: number | null;
}

export interface TokenDb {
  list: TokenDef[];
  byId: Map<string, TokenDef>;
  warnings: string[];
}

export function parseTokens(csvText: string): TokenDb {
  const warnings: string[] = [];
  const byId = new Map<string, TokenDef>();
  for (const { line, values } of csvToRecords(csvText)) {
    const id = (values.id || values.name || '').trim().toLowerCase().replace(/\s+/g, '-');
    if (!id) {
      warnings.push(`tokens.csv line ${line}: needs an id or a name, so the row was skipped.`);
      continue;
    }
    if (byId.has(id)) {
      warnings.push(`tokens.csv line ${line}: the id "${id}" is used twice; only the first one is kept.`);
      continue;
    }
    const bar = (values.bar || '').toLowerCase();
    const start = Number.parseInt(values.start, 10);
    const size = Number.parseFloat(values.size);
    const fullAt = Number.parseFloat(values.full_at ?? values['full at'] ?? '');
    byId.set(id, {
      id,
      name: values.name || id,
      category: (values.category || 'misc').toLowerCase(),
      image: values.image ?? '',
      counter: /^(y|yes|true|1)$/i.test(values.counter || ''),
      start: Number.isFinite(start) ? start : 1,
      bar: bar === 'gerzite' || bar === 'dark' ? bar : '',
      shape: /^cut/i.test(values.shape || '') ? 'cutout' : 'round',
      size: Number.isFinite(size) && size > 0 ? size : null,
      effect: (values.effect || id).trim().toLowerCase().replace(/\s+/g, '-'),
      fullAt: Number.isFinite(fullAt) && fullAt > 0 ? fullAt : null,
    });
  }
  return { list: [...byId.values()], byId, warnings };
}
